import React, { useState, useEffect } from 'react';
import { FiUsers, FiPlus, FiTrash2, FiEdit2, FiX, FiMail, FiUserPlus, FiSend, FiEye, FiClock, FiBarChart2 } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import TeamDashboard from './TeamDashboard';
import './Teams.css';
import './Digest.css';

const Teams = ({ onBack, onNavigate }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [upgradeModal, setUpgradeModal] = useState(null); // { message }
  const [showTeamDashboard, setShowTeamDashboard] = useState(false);

  // Digest state
  const [digestConfig, setDigestConfig] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestHistory, setDigestHistory] = useState([]);
  const [digestSending, setDigestSending] = useState(false);

  // Wiki state
  const [wikiArticles, setWikiArticles] = useState([]);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [showWikiEditor, setShowWikiEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [wikiForm, setWikiForm] = useState({ title: '', content: '', is_pinned: false });

  useEffect(() => {
    Promise.all([fetchCurrentUser(), fetchTeams()]);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/auth/me'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/teams'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${teamId}`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeamDetails(data);
      }
    } catch (err) {
      console.error('Failed to fetch team details:', err);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) {
      alert('Please enter a team name');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint('/api/teams'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: newTeamName.trim(),
          description: newTeamDescription.trim() || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        const detail = error.detail;
        if (response.status === 403 && detail?.upgrade_required) {
          setShowCreateModal(false);
          setUpgradeModal({ message: detail.message });
          return;
        }
        throw new Error(typeof detail === 'string' ? detail : 'Failed to create team');
      }

      setShowCreateModal(false);
      setNewTeamName('');
      setNewTeamDescription('');
      fetchTeams();
    } catch (err) {
      alert(`Failed to create team: ${err.message}`);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/invite`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to invite member');
      }

      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchTeamDetails(selectedTeam.id);
      fetchTeams();
    } catch (err) {
      alert(`Failed to invite member: ${err.message}`);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/members/${memberId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to remove member');
      }

      fetchTeamDetails(selectedTeam.id);
      fetchTeams();
    } catch (err) {
      alert(`Failed to remove member: ${err.message}`);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      // Check if we have a token
      if (!headers.Authorization) {
        alert('You are not authenticated. Please log in again.');
        window.location.reload();
        return;
      }

      const response = await fetch(getApiEndpoint(`/api/teams/${teamId}`), {
        method: 'DELETE',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Handle 401/403 errors specially
        if (response.status === 401) {
          alert('Your session has expired. Please log in again.');
          window.location.reload();
          return;
        }
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to delete team');
      }

      fetchTeams();
      if (selectedTeam && selectedTeam.id === teamId) {
        setSelectedTeam(null);
        setTeamDetails(null);
      }
    } catch (err) {
      alert(`Failed to delete team: ${err.message}`);
    }
  };

  // Wiki functions
  const fetchWiki = async (teamId) => {
    setWikiLoading(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/teams/${teamId}/wiki`), { headers: getAuthHeaders() });
      if (res.ok) setWikiArticles(await res.json());
    } catch (_) {}
    setWikiLoading(false);
  };

  const saveWikiArticle = async () => {
    if (!selectedTeam) return;
    const method = editingArticle ? 'PUT' : 'POST';
    const url = editingArticle
      ? getApiEndpoint(`/api/teams/${selectedTeam.id}/wiki/${editingArticle.id}`)
      : getApiEndpoint(`/api/teams/${selectedTeam.id}/wiki`);
    try {
      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(wikiForm),
      });
      if (res.ok) {
        fetchWiki(selectedTeam.id);
        setShowWikiEditor(false);
        setEditingArticle(null);
        setWikiForm({ title: '', content: '', is_pinned: false });
      }
    } catch (_) {}
  };

  const deleteWikiArticle = async (articleId) => {
    if (!selectedTeam) return;
    try {
      await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/wiki/${articleId}`), {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      setWikiArticles(prev => prev.filter(a => a.id !== articleId));
    } catch (_) {}
  };

  const handleViewTeam = (team) => {
    setSelectedTeam(team);
    setShowTeamDashboard(false);
    fetchTeamDetails(team.id);
    fetchDigestConfig(team.id);
    fetchDigestHistory(team.id);
    fetchWiki(team.id);
    setDigestPreview(null);
  };

  const fetchDigestConfig = async (teamId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${teamId}/digest/config`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDigestConfig(data);
      } else if (response.status === 404) {
        setDigestConfig(null);
      }
    } catch (err) {
      console.error('Failed to fetch digest config:', err);
      setDigestConfig(null);
    }
  };

  const fetchDigestHistory = async (teamId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${teamId}/digest/history?limit=5`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDigestHistory(data);
      }
    } catch (err) {
      console.error('Failed to fetch digest history:', err);
    }
  };

  const handleToggleDigest = async (enabled) => {
    if (!selectedTeam) return;
    setDigestLoading(true);
    try {
      const body = digestConfig
        ? { is_active: enabled }
        : { is_active: enabled, cadence: 'weekly', day_of_week: 1, recipients_mode: 'owners_only' };

      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/digest/config`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const data = await response.json();
        setDigestConfig(data);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update digest');
      }
    } catch (err) {
      alert(`Failed to update digest: ${err.message}`);
    } finally {
      setDigestLoading(false);
    }
  };

  const handleUpdateDigestConfig = async (updates) => {
    if (!selectedTeam) return;
    setDigestLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/digest/config`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const data = await response.json();
        setDigestConfig(data);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update digest');
      }
    } catch (err) {
      alert(`Failed to update digest: ${err.message}`);
    } finally {
      setDigestLoading(false);
    }
  };

  const handlePreviewDigest = async () => {
    if (!selectedTeam) return;
    setDigestLoading(true);
    setDigestPreview(null);
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/digest/preview?days=7`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDigestPreview(data);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to generate preview');
      }
    } catch (err) {
      alert(`Failed to preview digest: ${err.message}`);
    } finally {
      setDigestLoading(false);
    }
  };

  const handleSendDigest = async () => {
    if (!selectedTeam) return;
    if (!window.confirm('Send the weekly digest email now?')) return;
    setDigestSending(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/digest/send?days=7`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Digest sent to ${data.recipients?.length || 0} recipient(s)`);
        fetchDigestHistory(selectedTeam.id);
        fetchDigestConfig(selectedTeam.id);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to send digest');
      }
    } catch (err) {
      alert(`Failed to send digest: ${err.message}`);
    } finally {
      setDigestSending(false);
    }
  };

  const handleViewHistoryDigest = async (digestId) => {
    if (!selectedTeam) return;
    try {
      const response = await fetch(getApiEndpoint(`/api/teams/${selectedTeam.id}/digest/history/${digestId}`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDigestPreview(data);
      } else {
        alert('Failed to load digest data');
      }
    } catch (err) {
      alert(`Failed to load digest: ${err.message}`);
    }
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="teams-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teams-container">
      <div className="teams-header">
        <h2>Teams</h2>
        <button className="create-team-button" onClick={() => setShowCreateModal(true)}>
          <FiPlus /> Create Team
        </button>
      </div>

      <div className="teams-content">
        <div className="teams-list">
          {teams.length === 0 ? (
            <div className="empty-state">
              <FiUsers size={48} />
              <p>No teams yet. Create your first team to start collaborating!</p>
            </div>
          ) : (
            teams.map((team) => (
              <div
                key={team.id}
                className={`team-card ${selectedTeam?.id === team.id ? 'active' : ''}`}
                onClick={() => handleViewTeam(team)}
              >
                <div className="team-card-content">
                  <h3>{team.name}</h3>
                  <p className="team-meta">{team.member_count} members</p>
                  {team.description && <p className="team-description">{team.description}</p>}
                </div>
                {team.owner_id && (
                  <div className="team-owner-badge">Owner</div>
                )}
              </div>
            ))
          )}
        </div>

        {selectedTeam && teamDetails && showTeamDashboard && (
          <TeamDashboard
            teamId={teamDetails.id}
            teamName={teamDetails.name}
            onBack={() => setShowTeamDashboard(false)}
          />
        )}

        {selectedTeam && teamDetails && !showTeamDashboard && (
          <div className="team-details">
            <div className="team-details-header">
              <h3>{teamDetails.name}</h3>
              <button
                className="team-dashboard-btn"
                onClick={() => setShowTeamDashboard(true)}
                title="Lab Activity Dashboard"
              >
                <FiBarChart2 /> Lab Dashboard
              </button>
              {currentUser && teamDetails.owner_id === currentUser.id && (
                <button
                  className="delete-team-button"
                  onClick={() => handleDeleteTeam(teamDetails.id)}
                  title="Delete Team"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
            {teamDetails.description && (
              <p className="team-details-description">{teamDetails.description}</p>
            )}
            <p className="team-owner-info">Owner: {teamDetails.owner_username}</p>

            <div className="team-members-section">
              <div className="section-header">
                <h4>Members</h4>
                {currentUser && teamDetails.owner_id === currentUser.id && (
                  <button
                    className="invite-button"
                    onClick={() => setShowInviteModal(true)}
                  >
                    <FiUserPlus /> Invite Member
                  </button>
                )}
              </div>
              <div className="members-list">
                {teamDetails.members.map((member) => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      <div className="member-name">{member.username}</div>
                      <div className="member-email">{member.email}</div>
                    </div>
                    <div className="member-actions">
                      <span className={`member-role ${member.role}`}>{member.role}</span>
                      {currentUser && teamDetails.owner_id === currentUser.id && member.role !== 'owner' && (
                        <button
                          className="remove-member-button"
                          onClick={() => handleRemoveMember(member.id)}
                          title="Remove Member"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Digest Settings Section - Owner only */}
            {currentUser && teamDetails.owner_id === currentUser.id && (
              <div className="digest-section">
                <div className="section-header">
                  <h4>Weekly Digest</h4>
                </div>

                <div className="digest-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={digestConfig?.is_active || false}
                      onChange={(e) => handleToggleDigest(e.target.checked)}
                      disabled={digestLoading}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <label>{digestConfig?.is_active ? 'Enabled' : 'Disabled'}</label>
                </div>

                {digestConfig?.is_active && (
                  <div className="digest-settings">
                    <div className="digest-field">
                      <label>Send Day</label>
                      <select
                        value={digestConfig?.day_of_week ?? 1}
                        onChange={(e) => handleUpdateDigestConfig({ day_of_week: parseInt(e.target.value) })}
                        disabled={digestLoading}
                      >
                        {dayNames.map((day, i) => (
                          <option key={i} value={i}>{day}</option>
                        ))}
                      </select>
                    </div>

                    <div className="digest-field">
                      <label>Recipients</label>
                      <select
                        value={digestConfig?.recipients_mode || 'owners_only'}
                        onChange={(e) => handleUpdateDigestConfig({ recipients_mode: e.target.value })}
                        disabled={digestLoading}
                      >
                        <option value="owners_only">Owners only</option>
                        <option value="all_members">All members</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>

                    {digestConfig?.recipients_mode === 'custom' && teamDetails.members && (
                      <div className="custom-recipients-list">
                        {teamDetails.members.map((member) => (
                          <div key={member.user_id} className="custom-recipient-item">
                            <input
                              type="checkbox"
                              id={`recipient-${member.user_id}`}
                              checked={(digestConfig?.custom_recipient_ids || []).includes(member.user_id)}
                              onChange={(e) => {
                                const current = digestConfig?.custom_recipient_ids || [];
                                const updated = e.target.checked
                                  ? [...current, member.user_id]
                                  : current.filter(id => id !== member.user_id);
                                handleUpdateDigestConfig({ custom_recipient_ids: updated });
                              }}
                            />
                            <label htmlFor={`recipient-${member.user_id}`}>
                              {member.username} ({member.email})
                            </label>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="digest-info">
                      {digestConfig?.last_sent_at && (
                        <span className="digest-info-item">
                          <strong>Last sent:</strong> {new Date(digestConfig.last_sent_at).toLocaleDateString()}
                        </span>
                      )}
                      {digestConfig?.next_send_at && (
                        <span className="digest-info-item">
                          <strong>Next:</strong> {new Date(digestConfig.next_send_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    <div className="digest-actions">
                      <button
                        className="btn-preview"
                        onClick={handlePreviewDigest}
                        disabled={digestLoading}
                      >
                        <FiEye /> Preview Digest
                      </button>
                      <button
                        className="btn-send"
                        onClick={handleSendDigest}
                        disabled={digestSending || digestLoading}
                      >
                        <FiSend /> {digestSending ? 'Sending...' : 'Send Now'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Digest Preview */}
                {digestPreview && (
                  <div className="digest-preview">
                    <div className="digest-preview-header">
                      <h5>Digest Preview: {digestPreview.period_start?.slice(0, 10)} to {digestPreview.period_end?.slice(0, 10)}</h5>
                      <button className="digest-preview-close" onClick={() => setDigestPreview(null)}>Close</button>
                    </div>
                    <div className="digest-preview-body">
                      <div className="digest-stats-grid">
                        <div className="digest-stat-box">
                          <div className="digest-stat-value">{digestPreview.totals?.papers_added || 0}</div>
                          <div className="digest-stat-label">Papers</div>
                        </div>
                        <div className="digest-stat-box">
                          <div className="digest-stat-value">{digestPreview.totals?.collections_modified || 0}</div>
                          <div className="digest-stat-label">Collections</div>
                        </div>
                        <div className="digest-stat-box">
                          <div className="digest-stat-value">{digestPreview.totals?.chat_sessions || 0}</div>
                          <div className="digest-stat-label">Chats</div>
                        </div>
                        <div className="digest-stat-box">
                          <div className="digest-stat-value">{digestPreview.totals?.tasks_created || 0}</div>
                          <div className="digest-stat-label">Tasks</div>
                        </div>
                      </div>

                      {digestPreview.member_activity?.length > 0 && (
                        <>
                          <div className="digest-section-title">Member Activity</div>
                          <table className="digest-member-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Papers</th>
                                <th>Chats</th>
                                <th>Tasks</th>
                                <th>Notes</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {digestPreview.member_activity.map((m) => (
                                <tr key={m.user_id}>
                                  <td>{m.username}</td>
                                  <td>{m.papers_added}</td>
                                  <td>{m.chat_sessions}</td>
                                  <td>{m.action_items_created}</td>
                                  <td>{m.notes_created}</td>
                                  <td className={m.is_inactive ? 'status-inactive' : 'status-active'}>
                                    {m.is_inactive ? 'INACTIVE' : 'Active'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {digestPreview.overdue_items?.length > 0 && (
                        <>
                          <div className="digest-section-title overdue">Overdue Tasks ({digestPreview.overdue_items.length})</div>
                          {digestPreview.overdue_items.map((item) => (
                            <div key={item.id} className="digest-task-card overdue">
                              <div className="digest-task-title">{item.title}</div>
                              <div className="digest-task-meta">
                                {item.assignee_username || 'Unassigned'} &middot; Due: {item.due_date?.slice(0, 10) || 'N/A'}
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {digestPreview.outstanding_items?.length > 0 && (
                        <>
                          <div className="digest-section-title">Outstanding Tasks ({digestPreview.outstanding_items.length})</div>
                          {digestPreview.outstanding_items.map((item) => (
                            <div key={item.id} className="digest-task-card outstanding">
                              <div className="digest-task-title">{item.title}</div>
                              <div className="digest-task-meta">
                                {item.assignee_username || 'Unassigned'} &middot; {item.status} &middot; Due: {item.due_date?.slice(0, 10) || 'N/A'}
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {digestPreview.monitor_alerts?.length > 0 && (
                        <>
                          <div className="digest-section-title">Monitor Alerts</div>
                          {digestPreview.monitor_alerts.map((alert) => (
                            <div key={alert.monitor_id} className="digest-task-card outstanding">
                              <div className="digest-task-title">{alert.monitor_name}</div>
                              <div className="digest-task-meta">
                                {alert.owner_username} &middot; {alert.new_papers_count} new paper(s)
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {digestPreview.inactive_members?.length > 0 && (
                        <>
                          <div className="digest-section-title">Inactive Members</div>
                          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                            {digestPreview.inactive_members.join(', ')}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Digest History */}
                {digestHistory.length > 0 && (
                  <div className="digest-history">
                    <h5><FiClock style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />Recent Digests</h5>
                    <div className="digest-history-list">
                      {digestHistory.map((log) => (
                        <div
                          key={log.id}
                          className="digest-history-item"
                          onClick={() => handleViewHistoryDigest(log.id)}
                        >
                          <span className="digest-history-date">
                            {new Date(log.period_start).toLocaleDateString()} &mdash; {new Date(log.period_end).toLocaleDateString()}
                          </span>
                          <span className={`digest-history-badge ${log.triggered_by}`}>
                            {log.triggered_by === 'scheduled' ? 'Scheduled' : 'Manual'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Wiki / Knowledge Base */}
            <div className="wiki-section">
              <div className="section-header">
                <h4>Knowledge Base</h4>
                <button className="invite-button" onClick={() => {
                  setEditingArticle(null);
                  setWikiForm({ title: '', content: '', is_pinned: false });
                  setShowWikiEditor(true);
                }}>+ New Article</button>
              </div>

              {wikiLoading ? (
                <p className="team-loading-text">Loading...</p>
              ) : wikiArticles.length === 0 ? (
                <p className="team-empty-text">No articles yet. Create one to share lab procedures, methods, or key references with your team.</p>
              ) : (
                <div className="wiki-list">
                  {wikiArticles.map(article => (
                    <div key={article.id} className={`wiki-card ${article.is_pinned ? 'wiki-card--pinned' : ''}`}>
                      <div className="wiki-card-header">
                        <span className="wiki-card-title">
                          {article.is_pinned && '📌 '}{article.title}
                        </span>
                        <div className="wiki-card-actions">
                          <button onClick={() => {
                            setEditingArticle(article);
                            setWikiForm({ title: article.title, content: article.content, is_pinned: article.is_pinned });
                            setShowWikiEditor(true);
                          }}>Edit</button>
                          <button onClick={() => deleteWikiArticle(article.id)}>Delete</button>
                        </div>
                      </div>
                      <p className="wiki-card-preview">{(article.content || '').slice(0, 200)}{article.content?.length > 200 ? '...' : ''}</p>
                      <span className="wiki-card-meta">by {article.author_name} · {new Date(article.updated_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}

              {showWikiEditor && (
                <div className="wiki-editor">
                  <h5>{editingArticle ? 'Edit Article' : 'New Article'}</h5>
                  <input
                    className="wiki-input"
                    value={wikiForm.title}
                    onChange={e => setWikiForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Article title"
                  />
                  <textarea
                    className="wiki-textarea"
                    rows={10}
                    value={wikiForm.content}
                    onChange={e => setWikiForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write in markdown..."
                  />
                  <label className="wiki-pin-label">
                    <input
                      type="checkbox"
                      checked={wikiForm.is_pinned}
                      onChange={e => setWikiForm(prev => ({ ...prev, is_pinned: e.target.checked }))}
                    /> Pin to top
                  </label>
                  <div className="wiki-editor-actions">
                    <button className="invite-button" onClick={saveWikiArticle}>Save</button>
                    <button onClick={() => { setShowWikiEditor(false); setEditingArticle(null); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCreateModal(false)}>
              <FiX />
            </button>
            <h2>Create New Team</h2>
            <form onSubmit={handleCreateTeam}>
              <div className="form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Enter team name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  placeholder="Enter team description (optional)"
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit">Create Team</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upgrade Required Modal */}
      {upgradeModal && (
        <div className="modal-overlay" onClick={() => setUpgradeModal(null)}>
          <div className="modal upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setUpgradeModal(null)}>
              <FiX />
            </button>
            <div className="upgrade-modal-body">
              <div className="upgrade-modal-icon">🔒</div>
              <h2>Pro feature</h2>
              <p>{upgradeModal.message}</p>
              <div className="upgrade-modal-actions">
                <button className="upgrade-modal-cancel" onClick={() => setUpgradeModal(null)}>
                  Maybe later
                </button>
                <button
                  className="upgrade-modal-confirm"
                  onClick={() => { setUpgradeModal(null); onNavigate?.('membership'); }}
                >
                  Upgrade to Pro →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && selectedTeam && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowInviteModal(false)}>
              <FiX />
            </button>
            <h2>Invite Team Member</h2>
            <form onSubmit={handleInviteMember}>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value="member">Member</option>
                </select>
                <small>Note: Only one owner is allowed per team</small>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </button>
                <button type="submit">Invite</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;

